<i18n src="./resources/locales.json"></i18n>

<template>
  <v-data-table
    ref="table"
    class="surface elevation-1 lighten-1 custom-scrollbar"
    :headers="tableHeaders"
    :items="items"
    item-key="businessProcessId"
    :options="options"
    :loading="loading"
    :hide-default-header="hideDefaultHeader"
    :hide-default-footer="hideDefaultFooter"
    :disable-pagination="disablePagination"
    :disable-sort="disableSort"
    :custom-sort="customSort"
    :custom-filter="customFilter"
    :filter-options="filterOptions"
    :search="search"
    @update:options="onOptionsUpdate"
    @update:sort-by="onSort"
    @update:sort-desc="onSort"
    @current-items="currentItems"
  >
    <template #top>
      <slot name="top"></slot>
    </template>
    <template v-slot:item="row">
      <EcsTableRow
        :row="row"
        :id-field="idField"
        :context-menu-event="contextMenuEvent"
        :now="now"
        :app-time-zone-id="appTimeZoneId"
      />
    </template>
  </v-data-table>
</template>

<script>
// eslint-disable-next-line import/no-named-as-default
import Sortable from 'sortablejs';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import EcsTableRow from '@/components/ecs-table/EcsTableRow';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';
import { OPERATION_TYPE } from '@/constants/table';

export default {
  name: 'EcsTable',

  components: { EcsTableRow },

  mixins: [EcsTimeFormatter],

  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    height: {
      type: Number,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideDefaultHeader: {
      type: Boolean,
      default: false
    },
    hideDefaultFooter: {
      type: Boolean,
      default: true
    },
    disablePagination: {
      type: Boolean,
      default: false
    },
    disableSort: {
      type: Boolean,
      required: true
    },
    customSort: {
      type: Function,
      required: true
    },
    filterOptions: {
      type: Object,
      default: () => {}
    },
    filterable: {
      type: Boolean,
      required: true
    },
    search: {
      type: String,
      default: ''
    },
    customFilter: {
      type: Function,
      required: true
    },
    idField: {
      type: String,
      required: true
    },
    sortedBusinessProcessesIds: {
      type: Array,
      required: false,
      default: () => []
    },
    contextMenuEvent: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      now: moment().tz(this.getIanaTimeZone(this.appTimeZoneId)),
      nowInterval: null
    };
  },

  computed: {
    ...mapGetters('REFERENCES', ['availableTimeZones']),

    tableHeaders() {
      return this.headers.map(header => ({ ...header, align: 'start' }));
    },
    appTimeZoneId() {
      return this.availableTimeZones.find(tz => tz.value === this.applicationSettings.timeZoneId).id || {};
    },
    customFilterValues() {
      return this.headers.map(header => {
        return [...header.customFilter.value];
      });
    }
  },

  watch: {
    items() {
      this.$forceUpdate();
    },
    tableHeaders(newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        this.columnResizing();
      }
    },
    headers() {
      this.resizeStickyColumnHeader();
    },

    customFilterValues: {
      handler() {
        this.handleTooltips();
      },
      deep: true
    }
  },

  async mounted() {
    const vm = this;
    const options = {
      animation: 150,
      sort: true,
      draggable: '.ecs-table-row__draggable',
      onEnd({ to }) {
        const sortedBusinessProcessesIds = [];
        to.children.forEach(row => sortedBusinessProcessesIds.push(row.id));
        if (vm.search) {
          return;
        }
        vm.$emit(
          'sortedIdsChanged',
          sortedBusinessProcessesIds.filter(id => !!id)
        );
      }
    };
    Sortable.create(this.$refs.table.$el.getElementsByTagName('tbody')[0], options);

    this.nowInterval = setInterval(() => {
      this.updateNow();
    }, 1000);

    await this.$nextTick();
    this.columnResizing();
    this.resizeStickyColumnHeader();
  },

  beforeDestroy() {
    clearInterval(this.nowInterval);
  },

  methods: {
    currentItems(items) {
      const tableRef = this.$refs.table;
      this.$emit('currentItems', { items, tableRef });
    },
    onOptionsUpdate(value) {
      this.$emit('optionsUpdate', value);
    },
    onSort() {
      if (this.search) {
        return;
      }
      const sortedBusinessProcessesIds = this.getSortedBPIds();
      this.$emit('sortedIdsChanged', sortedBusinessProcessesIds);
    },
    getSortedBPIds() {
      const items = this.$refs.table.$children[0].computedItems;
      return items.map(({ businessProcessId }) => businessProcessId).filter(id => !!id);
    },

    columnResizing() {
      let thElm;
      let colElm;
      let startOffset;
      const root = this.$root;
      const thisEl = this.$el;

      Array.prototype.forEach.call(thisEl.querySelectorAll('th'), function(th) {
        // Don't add Resizor or Filter Icon/Tooltip element if it already exists
        if (!th.querySelector('div.column-resize')) {
          const thCol = th.parentNode.parentNode.previousElementSibling.querySelector(
            ':nth-child(' + (th.cellIndex + 1) + ')'
          );

          // Reassign style properties
          // th has the styles auto assigned from headers
          thCol.style.width = th.style.width;
          thCol.style.minWidth = th.style.width;
          thCol.style.maxWidth = th.style.width;

          if (th.cellIndex > 0) {
            th.style.position = 'relative';
            th.style.width = 'unset';
            th.style.minWidth = 'unset';
          }

          // Create Filter Icon element
          const filterIcon = document.createElement('div');
          filterIcon.innerHTML = '&nbsp;';
          filterIcon.classList.add('icon-filter');
          filterIcon.style.top = '50%';
          filterIcon.style.right = '10px';
          filterIcon.style.transform = 'translateY(-50%)';
          filterIcon.style.position = 'absolute';
          filterIcon.innerHTML = '<icon class="mdi mdi-filter"></icon>';
          th.appendChild(filterIcon);

          // Create Resizor element with event listeners
          const grip = document.createElement('div');
          grip.innerHTML = '&nbsp;';
          grip.style.top = 0;
          grip.style.right = 0;
          grip.style.bottom = 0;
          grip.style.width = '10px';
          grip.style.position = 'absolute';
          grip.style.cursor = 'col-resize';
          grip.classList.add('column-resize');
          grip.addEventListener('mousedown', function(e) {
            // Disable table Sort Temporally
            root.$emit('table-disable-sort');

            thElm = th;
            startOffset = thElm.offsetWidth - e.pageX;
            colElm = thElm.parentNode.parentNode.previousElementSibling.querySelector(
              ':nth-child(' + (thElm.cellIndex + 1) + ')'
            );
          });

          th.appendChild(grip);
        }
      });

      this.handleTooltips();

      document.addEventListener('mousemove', function(e) {
        if (thElm) {
          colElm.style.width = startOffset + e.pageX + 'px';
          colElm.style.minWidth = startOffset + e.pageX + 'px';
          colElm.style.maxWidth = startOffset + e.pageX + 'px';
        }
      });

      document.addEventListener('mouseup', function(e) {
        if (thElm) {
          // Re-enable Table Sort
          root.$emit('table-enable-sort');
          root.$emit('update-summary-table-col-width', thisEl, thElm.cellIndex, colElm.style.width);

          thElm = undefined;
          colElm = undefined;
        }
      });
    },

    resizeStickyColumnHeader() {
      const thisEl = this.$el;
      const thFirst = thisEl.querySelector('th:first-child');
      if (thFirst) {
        thFirst.style.height = thisEl.querySelector('th:nth-child(2)').offsetHeight + 'px';
      }
    },

    updateNow() {
      this.now = moment().tz(this.getIanaTimeZone(this.appTimeZoneId));
    },

    handleTooltips() {
      this.$el.querySelectorAll('th').forEach(th => {
        const iconFilter = th.querySelector('.icon-filter');
        const thSpan = th.querySelector('span');
        const thIconFilter = th.querySelector('.icon-filter');

        thSpan.title = this.headers[th.cellIndex].description;
        if (this.headers[th.cellIndex].customFilter.enabled) {
          iconFilter.classList.add('active');
          const customFilter = this.headers[th.cellIndex].customFilter;

          if (customFilter.operator.operationType === OPERATION_TYPE.INCLUDES) {
            const customFilterIncludes = customFilter.value.join(', ').replace(/([A-Z]+)*([A-Z][a-z])/g, '$1 $2');
            thIconFilter.title = `${this.$t('includes')}: ${customFilterIncludes}`;
          } else if (customFilter.operator.operationType === OPERATION_TYPE.RANGE) {
            const inRange = customFilter.inRange ? ' <= ' : ' > ';
            const rangeValue1 = customFilter.value[0] !== '' ? customFilter.value[0] + inRange : '';
            const rangeValue2 = customFilter.value[1] !== '' ? inRange + customFilter.value[1] : '';
            thIconFilter.title = `${this.$t('range')}: ${rangeValue1} x ${rangeValue2}`;
          }
        } else {
          iconFilter.classList.remove('active');
          thIconFilter.title = '';
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header {
  .text-start {
    font-size: 0.9rem;
  }

  .text-end {
    font-size: 0.9rem;
  }

  .icon-filter {
    display: none;

    &.active {
      display: block;
    }
  }
}
</style>
